<template>
  <div class="card card-body shadow-sm mt-0">

    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">{{ turma.id ? "Editar Turma" : "Nova Turma" }}</h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>
    <BaseForm
      :submitText="turma.id ? 'Salvar' : 'Salvar'"
      :cancelText="'Cancelar'"
      :isFormValid="isFormValid" 
      :isSubmitting="isSubmitting"
      @submit="salvarTurma" 
      @cancel="voltar"
    >

      <!-- Dados da Turma -->
      <FormSection title="Dados">
        <div class="d-flex justify-content-between gap-3">
          <!-- Campo Nível -->
          <div class="flex-grow-1" style="min-width: 250px;"> <!-- Largura mínima -->
            <SelectField
              id="nivel"
              v-model="turma.nivel_id"
              label="Nível <span class='required'>*</span>"
              :options="[
                { value: '', text: 'Selecione', disabled: true },
                ...niveis.map(nivel => ({ value: nivel.id, text: nivel.nivel }))
              ]"
              :class="{ 'error-input': errors.nivel_id }"
              required
              class="w-100" 
            />
          </div>

          <!-- Campo Instrutor -->
          <div class="flex-grow-1" style="min-width: 250px;">
            <SelectField
              id="instrutor"
              v-model="turma.instrutor_id"
              label="Instrutor <span class='required'>*</span>"
              :options="[
                { value: '', text: 'Selecione', disabled: true },
                ...instrutores.map(instrutor => ({ value: instrutor.id, text: instrutor.nome }))
              ]"
              :class="{ 'error-input': errors.instrutor_id }"
              required
              class="w-100"
            />
            <span v-if="errors.instrutor_id" class="error-message">{{ errors.instrutor_id }}</span>
          </div>
  
          <!-- Campo Ativo -->
          <div class="form-group">
            <label for="ativo" class="form-label">Ativo</label>
            <div class="form-switch d-flex align-items-center gap-2">
              <input
                type="checkbox"
                id="ativo"
                v-model="turma.ativo"
                class="form-check-input"
              />
              <label for="ativo" class="status-label mb-0">
                {{ turma.ativo ? "Sim" : "Não" }}
              </label>
            </div>
          </div>

        </div>
      </FormSection>
  
      <!-- Dias e Horários -->
      <FormSection title="Dias e Horários">
        <div class="d-flex flex-column align-items-center gap-3 w-75 mx-auto">
          <div
            v-for="(diaHorario, index) in turma.dias_horarios"
            :key="index"
            class="d-flex align-items-center gap-3 w-75"
          >
            <!-- SelectField para o Dia da Semana -->
            <SelectField
              v-model="diaHorario.dia_semana"
              :id="'dia_semana_' + index"
              label="Dia da Semana <span class='required'>*</span>"
              :options="[
                { value: '', text: 'Selecione', disabled: true },
                ...diasSemana.map(dia => ({ value: dia.value, text: dia.text }))
              ]"
              class="flex-grow-1"
              required
            />

            <!-- InputField para o Horário -->
            <InputField
              v-model="diaHorario.horario"
              type="time"
              :id="'horario_' + index"
              label="Horário <span class='required'>*</span>"
              class="flex-grow-1"
              required
            />

            <!-- Ícone para Remover o Dia e Horário -->
            <i
              class="bi bi-x-circle-fill text-danger remove-icon"
              @click="removerDiaHorario(index)"
              title="Remover"
              style="cursor: pointer; font-size: 1.5rem;"
            ></i>
          </div>

          <!-- Botão para Adicionar Novo Dia e Horário -->
          <div class="text-center">
            <i
              class="bi bi-plus-circle-fill text-primary add-icon"
              @click="adicionarDiaHorario"
              title="Adicionar"
              style="cursor: pointer; font-size: 1.8rem;"
            ></i>
          </div>
        </div>
      </FormSection>
 
    </BaseForm>
  </div>
</template>
    

<script>
import { reactive, ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import axios from "@/services/axios";
import FormSection from "@/components/Layout/FormSection.vue";
import SelectField from "@/components/Layout/SelectField.vue";
import InputField from "@/components/Layout/InputField.vue";
import BaseForm from "@/components/Layout/BaseForm.vue";

export default {
  components: { BaseForm, FormSection, SelectField, InputField },

  setup() {
    const router = useRouter();
    const route = useRoute();

    const turma = reactive({
      id: null,
      nivel_id: "",
      instrutor_id: "",
      ativo: true,
      dias_horarios: [], // Inicializado como array vazio
    });

    const niveis = ref([]);
    const instrutores = ref([]);
    const diasSemana = ref([
      { value: "segunda-feira", text: "Segunda-feira" },
      { value: "terça-feira", text: "Terça-feira" },
      { value: "quarta-feira", text: "Quarta-feira" },
      { value: "quinta-feira", text: "Quinta-feira" },
      { value: "sexta-feira", text: "Sexta-feira" },
      { value: "sábado", text: "Sábado" },
      { value: "domingo", text: "Domingo" },
    ]);

    const errors = reactive({});
    const isSubmitting = ref(false);

    const carregarNiveis = async () => {
      try {
        const response = await axios.get(`/cadastros/niveis`);
        niveis.value = response.data.map((nivel) => ({
          id: nivel.id,
          nivel: nivel.nivel,
        }));
      } catch (error) {
        console.error("Erro ao carregar níveis:", error);
      }
    };

    const carregarInstrutores = async () => {
        try {
            const response = await axios.get(`/cadastros/instrutores`);
            instrutores.value = response.data.map((instrutor) => ({
            id: instrutor.id,
            nome: instrutor.pessoa_fisica_nome, // Certifique-se de que o campo corresponde à resposta do back-end
            }));
        } catch (error) {
            console.error("Erro ao carregar instrutores:", error);
        }
    };

    const carregarTurma = async (id) => {
        try {
            const response = await axios.get(`/cadastros/turmas/${id}`);
            const data = response.data;

            turma.id = data.id;
            turma.nivel_id = data.nivel_id;
            turma.instrutor_id = data.instrutor_id;
            turma.ativo = data.ativo;

            // Mapeia os dias e horários
            turma.dias_horarios = data.dias_horarios.map((item) => ({
              dia_semana: item.dia_semana, // Agora usa diretamente o valor do banco de dados
              horario: item.horario
            }));
        } catch (error) {
            console.error("Erro ao carregar turma:", error);
        }
    };

    const adicionarDiaHorario = () => {
        if (!Array.isArray(turma.dias_horarios)) {
            turma.dias_horarios = []; // Inicializa como um array vazio se não for um array
        }

        turma.dias_horarios.push({
            dia_semana: "", // Define valores padrão para os novos campos
            horario: "",
        });
    };

    const removerDiaHorario = (index) => {
        turma.dias_horarios.splice(index, 1);
    };

    const validateForm = () => {
        // Reinicia os erros específicos dos dias e horários
        Object.keys(errors).forEach((key) => {
            if (key.startsWith('dias_horarios_') || key.startsWith('horario_')) {
            delete errors[key];
            }
        });

        // Valida o nível
        if (!turma.nivel_id) {
            errors.nivel_id = "O nível é obrigatório.";
        } else {
            delete errors.nivel_id;
        }

        // Valida o instrutor
        if (!turma.instrutor_id) {
            errors.instrutor_id = "O instrutor é obrigatório.";
        } else {
            delete errors.instrutor_id;
        }

        // Valida os dias e horários
        turma.dias_horarios.forEach((item, index) => {
            if (item.dia_semana || item.horario) {
            if (!item.dia_semana) {
                errors[`dias_horarios_${index}`] = `O dia da semana no item ${index + 1} é obrigatório.`;
            } else {
                delete errors[`dias_horarios_${index}`];
            }
            if (!item.horario) {
                errors[`horario_${index}`] = `O horário no item ${index + 1} é obrigatório.`;
            } else {
                delete errors[`horario_${index}`];
            }
            }
        });

        // Exibe erros no console apenas se existirem
        if (Object.keys(errors).length > 0) {
            console.log("Erros após validação:", errors);
        }
    };

    const isFormValid = computed(() => {
        const hasErrors = Object.values(errors).some((error) => error); // Verifica se há erros
        const hasValidDiasHorarios = turma.dias_horarios.every(
            (item) => item.dia_semana && item.horario // Verifica se todos os itens preenchidos estão válidos
        );

        const validNivel = Boolean(turma.nivel_id);
        const validInstrutor = Boolean(turma.instrutor_id);

        return !hasErrors && hasValidDiasHorarios && validNivel && validInstrutor;
    });

    const salvarTurma = async () => {
        if (isSubmitting.value) return;

        validateForm();

        // Verifica se existem erros antes de exibir o log
        if (!isFormValid.value) {
            if (Object.keys(errors).length > 0) { // Só exibe se houver erros
            console.error("O formulário contém erros:", errors);
            }
            return;
        }

        isSubmitting.value = true;

        try {
            const method = turma.id ? "put" : "post";
            const url = turma.id ? `/cadastros/turmas/${turma.id}` : "/cadastros/turmas";

            const response = await axios[method](url, {
            ...turma,
            dias_horarios: turma.dias_horarios, // Envia os dias e horários atualizados
            });

            const vinculosMigrados = Number(
              response?.data?.vinculos_migrados || 0
            );

            if (response?.data?.aviso) {
              alert(response.data.aviso);
            }

            if (vinculosMigrados > 0) {
              const textoAlunos =
                vinculosMigrados === 1
                  ? '1 aluno vinculado foi ajustado para o novo horário.'
                  : `${vinculosMigrados} alunos vinculados foram ajustados para o novo horário.`;

              alert(`Turma atualizada. ${textoAlunos}`);
            }

            // Limpa os erros após salvar com sucesso
            Object.keys(errors).forEach((key) => {
            delete errors[key];
            });

            router.push("/cadastros/turmas"); // Redireciona após salvar
        } catch (error) {
            console.error("Erro ao salvar turma:", error);

            if (error.response?.status === 409) {
              if (error.response?.data?.code === 'TURMA_COM_PENDENCIAS') turma.ativo = true;
              alert(
                error.response?.data?.message ||
                error.response?.data?.error ||
                'Esta alteração afeta alunos já vinculados. Ajuste um horário por vez.'
              );
            }
        } finally {
            isSubmitting.value = false;
        }
    };

const excluirTurma = async (id) => {
            if (!id) {
                console.error("ID da turma não foi fornecido.");
                return;
            }

            const confirmar = confirm("Tem certeza que deseja excluir esta turma?");
            if (!confirmar) {
                console.log("Ação de exclusão cancelada pelo usuário.");
                return;
            }

            try {
                await axios.delete(`/cadastros/turmas/${id}`);
                console.log("Turma excluída com sucesso!");

                // Recarregar a lista de turmas (se aplicável)
                if (typeof carregarTurma === "function") {
                    await carregarTurma();
                }
            } catch (error) {
                // Detalha a mensagem de erro no console
                console.error("Erro ao excluir a turma:", error.response?.data || error.message);

                // Opcional: Mostre uma mensagem amigável para o usuário
                alert("Não foi possível excluir a turma. Por favor, tente novamente mais tarde.");
            }
    };

    
    const voltar = () => {
      router.push("/cadastros/turmas");
    };

    onMounted(async () => {
      carregarNiveis();
      carregarInstrutores();

      const id = route.params.id;
      if (id) {
        await carregarTurma(id);
      }
    });

    return {
      turma,
      niveis,
      instrutores,
      diasSemana,
      errors,
      isFormValid,
      isSubmitting,
      salvarTurma,
      voltar,
      adicionarDiaHorario,
      removerDiaHorario,
      excluirTurma,
    };
  },
};
</script>

<style scoped>


.dias-horarios-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px; /* Espaçamento entre os itens */
}

.dia-horario-row {
  display: flex;
  align-items: center;
  gap: 10px; /* Espaçamento entre os campos */
  margin-bottom: 10px; /* Espaçamento entre as linhas */
  flex-wrap: nowrap; /* Garante que os elementos não quebrem a linha */
}

.dia-horario-pair {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 calc(50% - 10px); /* Define que cada item ocupa 50% menos o espaçamento */
  box-sizing: border-box; /* Inclui padding e bordas no tamanho total */
}

.dia-horario-pair {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 calc(50% - 10px);
  box-sizing: border-box;
}

.add-icon-container {
  margin-top: 10px;
  text-align: center;
}

.add-icon,
.remove-icon {
  font-size: 24px;
  cursor: pointer;
}

.add-icon:hover {
  color: #007bff; /* Azul para hover no ícone de adicionar */
}

.remove-icon:hover {
  color: #dc3545; /* Vermelho para hover no ícone de remover */
}


</style>
